import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import type { OphthalmologyRefractionValues } from './ophthalmologyRefractionConstants';

const HEADER_BG = '#cfe8fc';
const CELL_BG = '#dbeafe';

const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: CELL_BG,
    fontWeight: 700,
    fontSize: '0.9rem',
    '& fieldset': { borderColor: '#93c5fd' },
    '&:hover fieldset': { borderColor: '#3b82f6' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb' },
  },
  '& input': { textAlign: 'center', py: 1 },
};

export interface OphthalmologyRefractionTableFieldsProps {
  values: OphthalmologyRefractionValues;
  onChange: (next: OphthalmologyRefractionValues) => void;
  /** Exibe bloco de observações dentro da grade (prescrição de refração). */
  showNotes?: boolean;
}

const OphthalmologyRefractionTableFields: React.FC<OphthalmologyRefractionTableFieldsProps> = ({
  values,
  onChange,
  showNotes = true,
}) => {
  const set = (field: keyof OphthalmologyRefractionValues, value: string) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: '#cbd5e1' }}
    >
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: HEADER_BG }}>
            <TableCell colSpan={4} sx={{ fontWeight: 800, color: '#0f172a', py: 1 }}>
              Para longe.
            </TableCell>
          </TableRow>
          <TableRow sx={{ bgcolor: HEADER_BG }}>
            <TableCell sx={{ fontWeight: 700, color: '#0f172a', width: '28%' }} />
            <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
              Esférico
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
              Cilíndrico
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
              Eixo
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Olho Direito</TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="+0,00"
                value={values.odSph}
                onChange={(e) => set('odSph', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Esférico olho direito' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="−0,00"
                value={values.odCyl}
                onChange={(e) => set('odCyl', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Cilíndrico olho direito' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="°"
                value={values.odAxis}
                onChange={(e) => set('odAxis', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Eixo olho direito' }}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Olho Esquerdo</TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="+0,00"
                value={values.osSph}
                onChange={(e) => set('osSph', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Esférico olho esquerdo' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="−0,00"
                value={values.osCyl}
                onChange={(e) => set('osCyl', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Cilíndrico olho esquerdo' }}
              />
            </TableCell>
            <TableCell>
              <TextField
                fullWidth
                size="small"
                placeholder="°"
                value={values.osAxis}
                onChange={(e) => set('osAxis', e.target.value)}
                sx={cellInputSx}
                inputProps={{ 'aria-label': 'Eixo olho esquerdo' }}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ bgcolor: HEADER_BG }}>
            <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1.25 }}>
              Para perto: adicionar em refração de ambos os olhos no valor esférico de:
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 1.25, pb: showNotes ? 1 : 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <TextField
                  size="small"
                  placeholder="+0,00"
                  value={values.nearAdd}
                  onChange={(e) => set('nearAdd', e.target.value)}
                  sx={{ ...cellInputSx, width: { xs: '100%', sm: 180 } }}
                  inputProps={{ 'aria-label': 'Adição para perto' }}
                />
              </Box>
            </TableCell>
          </TableRow>
          {showNotes ? (
            <>
              <TableRow sx={{ bgcolor: HEADER_BG }}>
                <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1 }}>
                  Observações
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 0, pb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    placeholder="1. Medir DNP"
                    value={values.notes}
                    onChange={(e) => set('notes', e.target.value)}
                    inputProps={{ 'aria-label': 'Observações da refração' }}
                  />
                </TableCell>
              </TableRow>
            </>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OphthalmologyRefractionTableFields;
